import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import FormSignInModal from 'platform/forms/save-in-progress/FormSignInModal';
import {
  openHeaderSignIn,
  toggleFormSignInModal,
  toggleLoginModal,
} from 'platform/site-wide/user-nav/actions';
import { shouldConfirmLeavingCheck } from 'platform/site-wide/user-nav/containers/Main';
import {
  isLoggedIn,
  isProfileLoading,
  selectProfile,
} from 'platform/user/selectors';
import {
  isAuthenticatedWithSSOe,
  signInServiceName,
} from 'platform/user/authentication/selectors';
import { logoutUrl } from 'platform/user/authentication/utilities';
import {
  PAGE_PATH,
  SEARCH_APP_USED,
  SEARCH_LOCATION,
  SEARCH_SELECTION,
  SEARCH_TYPEAHEAD_ENABLED,
  addSearchGADataToStorage,
} from 'platform/site-wide/search-analytics';
import { logoutUrlSiS, logoutEvent } from 'platform/utilities/oauth/utilities';
import { OAUTH_KEYS } from 'platform/utilities/oauth/constants';
import { hasSession, hasSessionSSO } from 'platform/user/profile/utilities';
import localStorage from 'platform/utilities/storage/localStorage';
import OfficialGovtWebsite from '../OfficialGovtWebsite';
import VeteranCrisisLine from '../VeteranCrisisLine';
import addFocusBehaviorToCrisisLineModal from '../../../accessible-VCL-modal';
import { addOverlayTriggers } from '../../../legacy/menu';
import { selectMenuItemFilter } from '../../helpers/menuAccess';
import {
  HEADER_LOAD_TIMEOUT_MS,
  SIGN_IN_TIMEOUT_MS,
  reportHeaderError,
  reportHeaderNotLoaded,
  reportSignInNotOpened,
  watchHeaderLoad,
} from '../../helpers/monitoring';
import { headerShowsNavInBar } from '../../helpers/navInBar';
import BannerRows from './BannerRows';

// MPI names arrive in capitals. Title-cased here by hand, because lodash's
// startCase drops the apostrophe and hyphen from names like O'Brien-Smith.
const formatName = name =>
  name
    ?.trim()
    .toLowerCase()
    .replace(/(^|[\s'-])\p{L}/gu, letter => letter.toUpperCase()) || undefined;

// va-header-navigation leaves out the gov banner and the crisis line, so they
// are rendered above it the way production does: the mobile header's below the
// medium-screen breakpoint, the page shell's rows above it.
export const HeaderNavigation = ({
  megaMenuData,
  showMegaMenu,
  showNavLogin,
}) => {
  const el = useRef(null);
  const signInCheck = useRef(null);
  const dispatch = useDispatch();
  const loggedIn = useSelector(isLoggedIn);
  const profileLoading = useSelector(isProfileLoading);
  const profile = useSelector(selectProfile);
  // Same test as user-nav's SearchHelpSignIn: the session cookie flag says a
  // session exists before the profile request says who it is.
  const probablySignedIn = !loggedIn && profileLoading && !!hasSession();
  // While loading, SSOe is not yet known from the profile; the session flag
  // that sign-in wrote says which service it was.
  const isSSOe =
    useSelector(isAuthenticatedWithSSOe) ||
    (probablySignedIn && hasSessionSSO());
  const csp = useSelector(signInServiceName);
  const shouldConfirmLeavingForm = useSelector(state =>
    shouldConfirmLeavingCheck(state.form),
  );
  // Re-render on client-side route changes; the value itself comes from the
  // window, because the router's path is relative to the app's base.
  useSelector(state => state?.navigation?.route?.path);
  const showFormSignInModal = useSelector(
    state => !!state?.navigation?.showFormSignInModal,
  );

  const menuItemFilter = useSelector(selectMenuItemFilter);

  // Profiles with whitespace-only names exist, so an empty name is left unset.
  const firstName = formatName(profile?.userFullName?.first);
  const lastName = formatName(profile?.userFullName?.last);
  const email = profile?.email?.trim() || undefined;
  const user = useMemo(() => {
    if (probablySignedIn) {
      // The greeting selector caches the first name on every visit, so a
      // returning visitor's is known before the profile arrives.
      return {
        loading: true,
        firstName: formatName(localStorage.getItem('userFirstName')),
      };
    }
    if (!loggedIn) return undefined;
    // The welcome heading shows the first and last name, and falls back to
    // `name` for a profile with neither.
    return {
      firstName,
      lastName,
      name: email,
    };
  }, [probablySignedIn, loggedIn, firstName, lastName, email]);

  let signOutHref;
  if (loggedIn || probablySignedIn) {
    signOutHref = isSSOe
      ? logoutUrl()
      : logoutUrlSiS({ queryParams: { [OAUTH_KEYS.LOGOUT_TYPE]: 'user' } });
  }

  useEffect(() => {
    addFocusBehaviorToCrisisLineModal();
    addOverlayTriggers();
  }, []);

  useEffect(() => {
    const watch = watchHeaderLoad();
    const loadCheck = setTimeout(
      () => reportHeaderNotLoaded(el.current, watch),
      HEADER_LOAD_TIMEOUT_MS,
    );
    return () => {
      clearTimeout(loadCheck);
      watch.stop();
      clearTimeout(signInCheck.current);
    };
  }, []);

  const menuDataMissing = showMegaMenu && !megaMenuData?.length;
  useEffect(() => {
    if (menuDataMissing) reportHeaderError('Header menu data missing');
  }, [menuDataMissing]);

  // Object props are set as properties; the element reads no attribute for them.
  // A layout effect, so they are on the element before the browser paints:
  // the component's first render waits on its lazy chunk, and with menuData
  // unset it would render its packaged links, then swap to the page's.
  useLayoutEffect(() => {
    el.current.menuData = showMegaMenu ? megaMenuData : [];
    el.current.user = user;
    el.current.menuItemFilter = menuItemFilter;
  }, [megaMenuData, showMegaMenu, user, menuItemFilter]);

  useEffect(() => {
    const node = el.current;
    const onSignIn = () => {
      clearTimeout(signInCheck.current);
      signInCheck.current = setTimeout(
        reportSignInNotOpened,
        SIGN_IN_TIMEOUT_MS,
      );
      dispatch(openHeaderSignIn(shouldConfirmLeavingForm));
    };
    const onSignOut = () =>
      logoutEvent(csp, { shouldWait: !isSSOe, duration: 350 });

    // Search is a link to the search page, where the query is typed. The page
    // it was clicked on is saved for the search app, the way the header's
    // search box saved it, so the search is still reported as a header search.
    const onAnalytics = ({ detail }) => {
      if (detail?.details?.control !== 'search') return;
      addSearchGADataToStorage({
        [PAGE_PATH]: document.location.pathname,
        [SEARCH_LOCATION]: headerShowsNavInBar(node)
          ? 'Desktop Header Search'
          : 'Mobile Header Search',
        [SEARCH_APP_USED]: false,
        [SEARCH_SELECTION]: 'All VA.gov',
        [SEARCH_TYPEAHEAD_ENABLED]: false,
      });
    };

    node.addEventListener('vaSignInClick', onSignIn);
    node.addEventListener('vaSignOutClick', onSignOut);
    node.addEventListener('component-library-analytics', onAnalytics);
    return () => {
      node.removeEventListener('vaSignInClick', onSignIn);
      node.removeEventListener('vaSignOutClick', onSignOut);
      node.removeEventListener('component-library-analytics', onAnalytics);
    };
  }, [dispatch, shouldConfirmLeavingForm, csp, isSSOe]);

  return (
    <div className="vads-u-display--flex vads-u-flex-direction--column vads-u-margin--0 vads-u-padding--0">
      <div className="medium-screen:vads-u-display--none">
        <OfficialGovtWebsite />
        <VeteranCrisisLine id="header-crisis-line" />
      </div>
      <div className="vads-u-display--none medium-screen:vads-u-display--block">
        <BannerRows />
      </div>
      <va-header-navigation
        ref={el}
        data-dd-action-name="Header navigation"
        sign-out-href={signOutHref}
        current-href={window.location.pathname}
        hide-sign-in={showNavLogin ? undefined : ''}
      />
      {/* The leave-form confirmation user-nav's Main renders lives inside the
          hidden legacy header now, so the visible header renders its own. */}
      <FormSignInModal
        visible={showFormSignInModal}
        onClose={() => dispatch(toggleFormSignInModal(false))}
        onSignIn={() => dispatch(toggleLoginModal(true))}
      />
    </div>
  );
};

HeaderNavigation.propTypes = {
  megaMenuData: PropTypes.arrayOf(PropTypes.object).isRequired,
  showMegaMenu: PropTypes.bool.isRequired,
  showNavLogin: PropTypes.bool.isRequired,
};

export default HeaderNavigation;
